import { Box, Typography, Paper, Button, Accordion, AccordionSummary, AccordionDetails, Chip, Divider, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import HeaderBar from "../components/HeaderBar";
import themeColors from "../theme";

// Sample data - this would come from an API in production
const clinicalUpdatesData = {
    "streptococcal-pharyngitis": {
        title: "Streptococcal Pharyngitis",
        organization: "Infectious Diseases Society of America",
        date: "August 2026",
        officialGuidelineUrl: "https://pubmed.ncbi.nlm.nih.gov/41343363/",
        clinicalQuestion: "Diagnostic testing for group A streptococcal (GAS) pharyngitis",
        keyTakeaways: {
            summary: "Highlights",
            description: "",
            levels: [
                {
                    level: "Key Points",
                    label: "highlights",
                    color: "#2563eb",
                    bgColor: "#eff6ff",
                    borderColor: "#bfdbfe",
                    items: [
                        "Clinical scoring systems are most helpful for identifying patients with a low probability of GAS pharyngitis, for whom diagnostic testing is unlikely to be helpful.",
                        "High-risk patients should be strongly considered for diagnostic testing even when their clinical score is low.",
                        "The recommendation to use a clinical scoring system does not apply to children younger than 3 years."
                    ]
                }
            ],
            againstRecommendations: [],
            providerNotes: []
        },
        detailedRecommendations: [
            {
                header: "High-risk individuals",
                items: [
                    "Patients with a sore throat and household exposure to GAS.",
                    "Patients with a history of rheumatic fever.",
                    "Patients with signs or symptoms of complicated local or systemic infection, including peritonsillar abscess, retropharyngeal abscess, scarlet fever, or toxic shock syndrome."
                ]
            },
            {
                header: "Clinical scoring systems",
                items: [
                    "No particular clinical scoring system is recommended over another. Options include Centor, McIsaac, and FeverPAIN."
                ]
            }
        ]
    },
    "pediatric-influenza": {
        title: "Pediatric Influenza",
        organization: "American Academy of Pediatrics",
        date: "August 2026",
        officialGuidelineUrl: "https://pubmed.ncbi.nlm.nih.gov/42572140/",
        clinicalQuestion: "Management of influenza in pediatric patients",
        keyTakeaways: {
            summary: "Highlights",
            description: "",
            levels: [
                {
                    level: "Key Points",
                    label: "highlights",
                    color: "#2563eb",
                    bgColor: "#eff6ff",
                    borderColor: "#bfdbfe",
                    items: [
                        "In patients with confirmed or suspected influenza, antiviral treatment should be initiated in hospitalized patients or in those with severe or progressive symptoms.",
                        "In patients with confirmed or suspected influenza, antiviral treatment should be offered to those at high risk of complications from influenza, regardless of the duration of illness. Refer to Table 2 below for a list of high-risk groups."
                    ]
                }
            ],
            againstRecommendations: [],
            providerNotes: []
        },
        detailedRecommendations: [
            {
                header: "Influenza vaccination",
                items: ["Influenza vaccination is recommended for children 6 months of age or older."]
            },
            {
                header: "Preferred antiviral",
                items: ["Oseltamivir remains, per the AAP, the preferred antiviral for patients with influenza A and/or B."]
            },
            {
                header: "Patients not at high risk",
                items: ["Clinicians may offer antiviral therapy to patients with confirmed or suspected influenza who are not at high risk for influenza complications if therapy can be initiated within 48 hours of symptom onset."]
            }
        ],
        highRiskGroups: [
            { category: "Demographic characteristics", descriptions: ["Children younger than 5 years, especially those younger than 2 years (a)", "Children born preterm or early term (b)", "Residents of a chronic care facility or nursing home"] },
            { category: "Chronic pulmonary disease", descriptions: ["Asthma", "Cystic fibrosis", "Bronchopulmonary dysplasia", "Compromised respiratory function (e.g., requiring mechanical ventilation, tracheostomy)"] },
            { category: "Cardiovascular disease", descriptions: ["Hemodynamically significant conditions (excluding hypertension alone)"] },
            { category: "Kidney disease", descriptions: ["Chronic kidney disease, including end-stage kidney disease", "Dialysis"] },
            { category: "Hepatic disease", descriptions: ["Chronic liver disease", "Cirrhosis"] },
            { category: "Hematologic disease", descriptions: ["Sickle cell disease", "Other hemoglobinopathies"] },
            { category: "Metabolic disorders", descriptions: ["Diabetes mellitus"] },
            { category: "Neurologic and neurodevelopmental conditions", descriptions: ["Cerebral palsy", "Epilepsy", "Stroke", "Intellectual developmental disorder", "Moderate to severe developmental delay", "Neuromuscular disorders, including muscular dystrophy", "Spinal cord injury"] },
            { category: "Extreme obesity", descriptions: ["BMI ≥40 for adults; BMI >the 95th percentile in children (d)"] },
            { category: "Immunosuppression", descriptions: ["Receipt of immunocompromising medications", "Receipt of hematopoietic cell transplant or solid organ transplant", "Congenital or acquired immune deficiency, including HIV", "Asplenia"] },
            { category: "Other high-risk groups", descriptions: ["Receiving treatment with aspirin or salicylate-containing therapies (e)", "Pregnancy and up to 2 weeks postpartum"] }
        ],
        highRiskNotes: [
            "Abbreviation: BMI, body mass index.",
            "(a) Regardless of the presence of underlying medical conditions.",
            "(b) Children who were born preterm (birth at <37 weeks' gestation) or early term (birth at 37 or 38 weeks' gestation) are at higher risk of influenza hospitalization in the first 5 years of life.",
            "(c) The list of examples of underlying conditions or treatments is not exhaustive.",
            "(d) The BMI associated with increased risk is not well-defined in children, but could consider BMI at or above the 95th percentile for children and adolescents of the same age and sex.",
            "(e) Applies to children and adolescents younger than 19 years who are at increased risk of Reye syndrome."
        ]
    },
    "headache-management-ed": {
        title: "Headache Management in the ED",
        organization: "American Headache Society",
        date: "December 2025",
        officialGuidelineUrl: "https://headachejournal.onlinelibrary.wiley.com/doi/10.1111/head.70016",
        clinicalQuestion: "Management of acute migraine in the emergency department",
        keyTakeaways: {
            summary: "Highlights",
            description: "",
            levels: [
                {
                    level: "Key Points",
                    label: "highlights",
                    color: "#2563eb",
                    bgColor: "#eff6ff",
                    borderColor: "#bfdbfe",
                    items: [
                        "IV Prochlorperazine and Ganglion occipital nerve block are first line therapies for patients presenting to the ED with migraines. Second line treatment options include IV Metoclopramide, IV Ketorolac, and SC Sumatriptan."
                    ]
                }
            ],
            againstRecommendations: [],
            providerNotes: []
        },
        detailedRecommendationsIntro: "The American Headache Society (AHS) set forth new recommendations for the management of patients with migraines presenting to the ED.",
        detailedRecommendations: [
            {
                header: "Level A (must be offered)",
                items: [
                    "Prochlorperazine 10-12.5mg IV",
                    "Ganglion occipital nerve block"
                ]
            },
            {
                header: "Level B (should be offered)",
                items: [
                    "Metoclopramide: 10mg IV",
                    "Ketorolac: 30-60mg IV",
                    "Sumatriptan: 3-6mg SC",
                    "Dexamethasone: 8-16mg IV — Recommendation is based on its use for migraine prevention"
                ]
            },
            {
                header: "Level C (may be offered)",
                items: [
                    "Chlorpromazine: 12.5-25mg IV",
                    "Droperidol: 2.5-5mg IM",
                    "Haloperidol: 5mg IV",
                    "Dexamethasone: 8-16mg IV — Recommendation is based on its use for migraine treatment"
                ]
            },
            {
                header: "Level U (no recommendation)",
                items: [
                    "Normal saline",
                    "Magnesium"
                ]
            },
            {
                header: "Recommendations Against Use",
                items: [
                    "There also exists a Level A recommendation (must not be offered) against the use of hydromorphone, and a Level C recommendation (may not offer) against the use of morphine and diphenhydramine."
                ]
            },
            {
                header: "Provider Reminders",
                items: [
                    "The AHS does remind providers that the use of dopamine antagonists (e.g. prochlorperazine, metoclopramide, etc.) should be given to patients at low risk for extrapyramidal symptoms.",
                    "Similarly, providers should ideally administer steroids only in patients with low cumulative steroid exposure."
                ]
            }
        ]
    },
    "new-stroke-guidelines": {
        title: "Early Management of Acute Ischemic Stroke",
        organization: "American Heart Association/American Stroke Association",
        date: "January 2026",
        officialGuidelineUrl: "https://www.ahajournals.org/doi/10.1161/STR.0000000000000513",
        sourceUrl: "https://www.ahajournals.org/doi/10.1161/STR.0000000000000513?utm_campaign=sciencenews25-26&utm_source=science-news&utm_medium=phd-link&utm_content=phd-1-26-26#sec-7-5",
        clinicalQuestion: "IV thrombolysis window, glucose management",
        keyTakeaways: {
            summary: "Highlights",
            description: "",
            levels: [
                {
                    level: "Key Points",
                    label: "highlights",
                    color: "#2563eb",
                    bgColor: "#eff6ff",
                    borderColor: "#bfdbfe",
                    items: [
                        "Extended thrombolysis window: IV thrombolysis may be considered beyond 4.5 hours when advanced imaging shows viable tissue.",
                        "Mild non-disabling deficits: IV thrombolysis is not recommended for mild non-disabling deficits (e.g., isolated sensory symptoms) (Level B-R).",
                        "Glucose control: Treat persistent hyperglycemia to maintain 140–180 mg/dL."
                    ]
                }
            ],
            againstRecommendations: [],
            providerNotes: []
        },
        detailedRecommendations: [
            {
                header: "Glucose management",
                items: [
                    "It is not recommended to use IV insulin to achieve blood glucose levels of 80-130 mg/dL (Level A)"
                ]
            },
            {
                header: "Extended window for intravenous thrombolysis",
                items: [
                    "Patients may benefit from intravenous thrombolysis up to 9 hours after symptom onset if perfusion imaging demonstrates salvageable ischemic penumbra (Level B-R).",
                    "Patients with a large vessel occlusion, who cannot receive endovascular thrombectomy, may still benefit from intravenous thrombolysis up to 24 hours after symptom onset if perfusion imaging demonstrates salvageable ischemic penumbra (Level B-R)"
                ]
            }
        ]
    },
    "early-management-acute-ischemic-stroke": {
        title: "Early Management of Acute Ischemic Stroke",
        organization: "American Heart Association/American Stroke Association",
        date: "January 2026",
        officialGuidelineUrl: "https://www.ahajournals.org/doi/10.1161/STR.0000000000000513",
        sourceUrl: "https://www.ahajournals.org/doi/10.1161/STR.0000000000000513?utm_campaign=sciencenews25-26&utm_source=science-news&utm_medium=phd-link&utm_content=phd-1-26-26#sec-7-5",
        clinicalQuestion: "IV thrombolysis window, glucose management",
        keyTakeaways: {
            summary: "Highlights",
            description: "",
            levels: [
                {
                    level: "Key Points",
                    label: "highlights",
                    color: "#2563eb",
                    bgColor: "#eff6ff",
                    borderColor: "#bfdbfe",
                    items: [
                        "Extended thrombolysis window: IV thrombolysis may be considered beyond 4.5 hours when advanced imaging shows viable tissue.",
                        "Mild non-disabling deficits: IV thrombolysis is not recommended for mild non-disabling deficits (e.g., isolated sensory symptoms) (Level B-R).",
                        "Glucose control: Treat persistent hyperglycemia to maintain 140–180 mg/dL."
                    ]
                }
            ],
            againstRecommendations: [],
            providerNotes: []
        },
        detailedRecommendations: [
            {
                header: "Glucose management",
                items: [
                    "It is not recommended to use IV insulin to achieve blood glucose levels of 80-130 mg/dL (Level A)"
                ]
            },
            {
                header: "Extended window for intravenous thrombolysis",
                items: [
                    "Patients may benefit from intravenous thrombolysis up to 9 hours after symptom onset if perfusion imaging demonstrates salvageable ischemic penumbra (Level B-R).",
                    "Patients with a large vessel occlusion, who cannot receive endovascular thrombectomy, may still benefit from intravenous thrombolysis up to 24 hours after symptom onset if perfusion imaging demonstrates salvageable ischemic penumbra (Level B-R)"
                ]
            }
        ]
    },
    "acute-pe-management": {
        title: "Acute PE Management",
        organization: "American College of Cardiology/American Heart Association",
        date: "February 2026",
        officialGuidelineUrl: "https://www.ahajournals.org/doi/10.1161/CIR.0000000000001415",
        sourceUrl: "https://www.ahajournals.org/doi/10.1161/CIR.0000000000001415",
        clinicalQuestion: "Evaluation and Management of Acute Pulmonary Embolism in Adults",
        keyTakeaways: {
            summary: "Highlights",
            description: "",
            levels: [
                {
                    level: "Key Points",
                    label: "highlights",
                    color: "#2563eb",
                    bgColor: "#eff6ff",
                    borderColor: "#bfdbfe",
                    items: [
                        "Classification of PE: Risk stratification has shifted toward a category-based approach that emphasizes individualized assessment.",
                        "DOAC: In the absence of contraindications, DOACs are recommended over vitamin K antagonist in the treatment of acute PE (COR 1, LOE B-R).",
                        "Parental anticoagulation: Low molecular weight heparin is recommended over unfractionated heparin in patients with acute PE requiring parental therapy (COR 1, LOE B-R).",
                        "Breastfeeding: To prevent potential bleeding in breastfeeding infants, low molecular weight heparin, unfractionated heparin, or warfarin are recommended over DOACs for the treatment of acute PE (COR 1, LOE B-R).",
                        "Recurrent PE: In patients with recurrent PE who are adherent to their prescribed therapeutic anticoagulation, it is reasonable to change to a different drug class (COR 2a, LOE C-EO)."
                    ]
                }
            ],
            againstRecommendations: [],
            providerNotes: []
        },
        detailedRecommendations: [
            {
                header: "Classification of PE",
                items: [
                    "In 2011, pulmonary embolism was stratified into three categories: low risk, submassive, and massive. By 2019, this framework evolved into a more granular classification consisting of low risk, intermediate–low risk, intermediate–high risk, and high risk. The current guidelines support the use of a category-based approach."
                ]
            },
            {
                header: "Pregnancy",
                items: [
                    "In pregnant patients with an acute PE, low molecular weight heparin or unfractionated heparin are recommended (COR 1, LOE C-LD).",
                    "In pregnant patients with an acute PE, DOACs and warfarins may result in miscarriages and fetal anomalies (COR 3, LOE C-LD)."
                ]
            },
            {
                header: "Classification Reference",
                items: [
                    "COR (Class of Recommendation): Class 1 — Strong; Class 2a — Moderate benefit; Class 2b — Weak benefit; Class 3 — Harm.",
                    "LOE (Level of Evidence): Level A — High quality of evidence; Level B-R — Moderate quality of evidence; Level C-LD — Limited data; Level C-EO — Expert opinion."
                ]
            }
        ]
    },
    "hepatic-encephalopathy": {
        title: "Hepatic Encephalopathy",
        organization: "American College of Gastroenterology",
        date: "March 2026",
        officialGuidelineUrl: "https://journals.lww.com/ajg/fulltext/2026/03000/acg_clinical_guideline__hepatic_encephalopathy.12.aspx",
        sourceUrl: "https://journals.lww.com/ajg/fulltext/2026/03000/acg_clinical_guideline__hepatic_encephalopathy.12.aspx",
        clinicalQuestion: "Work up and Management of Hepatic Encephalopathy",
        keyTakeaways: {
            summary: "Highlights",
            description: "",
            levels: [
                {
                    level: "Key Points",
                    label: "highlights",
                    color: "#2563eb",
                    bgColor: "#eff6ff",
                    borderColor: "#bfdbfe",
                    items: [
                        "Ammonia blood level alone should not be used to rule in the diagnosis of hepatic encephalopathy.",
                        "For patients with hepatic encephalopathy, rifaximin should be added to the treatment rather than using lactulose alone.",
                        "Both key points are cited as conditional recommendations with a very low certainty of evidence."
                    ]
                }
            ],
            againstRecommendations: [],
            providerNotes: []
        },
        detailedRecommendations: [
            {
                header: "Diagnostic work up",
                items: [
                    "In the absence of alterations in behavior or alertness, isolated asterixis is not enough to diagnose patients with hepatic encephalopathy.",
                    "A normal blood ammonia level in cirrhotic patients with altered mental status should prompt consideration of other diagnoses in addition to hepatic encephalopathy.",
                    "Ammonia levels do not correlate with severity of hepatic encephalopathy.",
                    "In the absence of new-onset focal neurologic deficits, routine brain imaging is not recommended in cirrhotic patients presenting with confusion (conditional recommendation, very low certainty of evidence)."
                ]
            },
            {
                header: "Management",
                items: [
                    "Polyethylene glycol is a recognized alternative to lactulose (conditional recommendation, low certainty of evidence)."
                ]
            }
        ]
    },
    "life-threatening-bleeds-itp": {
        title: "Life threatening bleeds in ITP",
        organization: "McMaster ITP Emergency Management Guideline Group",
        date: "February 2026",
        officialGuidelineUrl: "https://pubmed.ncbi.nlm.nih.gov/41637560/",
        sourceUrl: "https://pubmed.ncbi.nlm.nih.gov/41637560/",
        clinicalQuestion: "Management of critical bleeds in patients with ITP",
        keyTakeaways: {
            summary: "Highlights",
            description: "",
            levels: [
                {
                    level: "Key Points",
                    label: "highlights",
                    color: "#2563eb",
                    bgColor: "#eff6ff",
                    borderColor: "#bfdbfe",
                    items: [
                        "Patients with ITP with platelet counts <20,000 (per microliter) who present with critical bleeds in the emergency department should receive IV high-dose corticosteroids, high-dose IVIG, platelet transfusion, and tranexamic acid."
                    ]
                }
            ],
            againstRecommendations: [],
            providerNotes: []
        },
        detailedRecommendations: [
            {
                header: "Definition of critical bleeds",
                items: [
                    "Critical bleeds are defined as life-threatening bleeds or those with the potential to cause significant morbidity.",
                    "Critical sites include intracranial, intraspinal, intraocular (particularly if there is concern for vision loss), retroperitoneal, pericardial, and intramuscular (particularly if there is concern for compartment syndrome).",
                    "Critical bleeds are also those that may result in hemodynamic or respiratory compromise."
                ]
            },
            {
                header: "Dosing in Adults",
                items: [
                    "High-dose corticosteroids: IV dexamethasone 40 mg/day for 4 days.",
                    "High-dose corticosteroids: IV methylprednisolone 1 g/day for 3 days.",
                    "High-dose IVIG: 1 g/kg for 1 or 2 consecutive days.",
                    "Platelet transfusion: Amount of transfusions should be based on clinical reassessment.",
                    "Tranexamic acid: 1 g every 6-8 hours for 1-3 days."
                ]
            },
            {
                header: "Dosing in Pediatrics",
                items: [
                    "High-dose corticosteroids: IV dexamethasone 0.6 mg/kg with a maximum dose of 40 mg per day.",
                    "High-dose corticosteroids: IV methylprednisolone 30 mg/kg with a maximum dose of 1 g per day.",
                    "High-dose IVIG: 0.8-1.0 g/kg for 1 or 2 consecutive days.",
                    "Platelet transfusion: Amount of transfusions should be based on clinical reassessment.",
                    "Tranexamic acid: 10-15 mg/kg IV with a maximum dose of 1 g every 8 hours for 1-3 days."
                ]
            }
        ]
    },
    "acute-low-back-pain": {
        title: "Management of Acute Low Back Pain",
        organization: "Delphi",
        date: "May 2026",
        officialGuidelineUrl: "https://www.paininnovation.net/albp-delphi-report",
        sourceUrl: "https://www.paininnovation.net/albp-delphi-report",
        clinicalQuestion: "Management of acute low back pain in the emergency department",
        keyTakeaways: {
            summary: "Highlights",
            description: "",
            levels: [
                {
                    level: "Key Points",
                    label: "highlights",
                    color: "#2563eb",
                    bgColor: "#eff6ff",
                    borderColor: "#bfdbfe",
                    items: [
                        "NSAIDs and muscle relaxants are first line pharmacological options.",
                        "Opioids are generally ineffective for function recovery.",
                        "There is insufficient evidence to support the use of systemic steroids. If used, they should be limited for radicular pain."
                    ]
                }
            ],
            againstRecommendations: [],
            providerNotes: []
        },
        detailedRecommendations: [
            {
                header: "Acute radiculopathy",
                items: [
                    "The most heavily debated recommendation is the management of acute radiculopathy. The evidence currently supports the use of local epidural steroid injections for acute radiculopathy over systemic steroids."
                ]
            },
            {
                header: "Imaging",
                items: [
                    "Imaging for acute low back pain should be reserved for patients with red flag features, or those where there is a concern for infection or fracture.",
                    "Red flag features: Bowel or bladder dysfunction, new or progressive numbness or weakness of the extremities."
                ]
            }
        ]
    }
};

export default function ClinicalUpdateDetailPage() {
    const { slug } = useParams();
    const navigate = useNavigate();
    const { teal, lightGray } = themeColors;

    const update = clinicalUpdatesData[slug];

    if (!update) {
        return (
            <Box sx={{ minHeight: "100vh", backgroundColor: lightGray }}>
                <HeaderBar />
                <Box sx={{ px: { xs: 2, md: 8 }, py: 6 }}>
                    <Typography variant="h5">Update not found</Typography>
                    <Button onClick={() => navigate("/clinical-updates")} sx={{ mt: 2 }}>
                        Back to Clinical Updates
                    </Button>
                </Box>
            </Box>
        );
    }

    return (
        <Box sx={{ minHeight: "100vh", backgroundColor: lightGray }}>
            <HeaderBar />

            <Box sx={{ px: { xs: 2, md: 8 }, py: 4 }}>
                {/* Back link and Official Guideline button */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Button
                        startIcon={<ArrowBackIcon />}
                        onClick={() => navigate("/clinical-updates")}
                        sx={{
                            color: teal,
                            textTransform: 'none',
                            '&:hover': { backgroundColor: 'transparent', textDecoration: 'underline' }
                        }}
                    >
                        Clinical Archive
                    </Button>
                    <Button
                        variant="outlined"
                        endIcon={<OpenInNewIcon />}
                        href={update.officialGuidelineUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                            color: teal,
                            borderColor: teal,
                            textTransform: 'none',
                            '&:hover': {
                                backgroundColor: `${teal}10`,
                                borderColor: teal
                            }
                        }}
                    >
                        View Official Guideline
                    </Button>
                </Box>

                {/* Title */}
                <Typography variant="h3" fontWeight="bold" color="#2C3E50" mb={0.5}>
                    {update.title}
                </Typography>
                <Typography variant="body1" color="#6C757D" mb={4}>
                    {update.organization} · {update.date}
                </Typography>

                {/* Clinical Question Addressed */}
                <Paper
                    elevation={0}
                    sx={{
                        p: 3,
                        mb: 3,
                        borderRadius: 2,
                        backgroundColor: '#f8f9fa',
                        border: '1px solid',
                        borderColor: 'divider'
                    }}
                >
                    <Typography variant="subtitle1" fontWeight="600" mb={1}>
                        Clinical Topic Addressed
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                        {update.clinicalQuestion}
                    </Typography>
                </Paper>

                {/* Highlights */}
                <Paper
                    elevation={0}
                    sx={{
                        p: 3,
                        mb: 3,
                        borderRadius: 2,
                        backgroundColor: '#fffbeb',
                        border: '1px solid',
                        borderColor: '#fef3c7'
                    }}
                >
                    <Typography variant="subtitle1" fontWeight="600" mb={1}>
                        Highlights
                    </Typography>
                    {update.keyTakeaways.description && (
                        <Typography variant="body1" mb={3} color="text.secondary">
                            {update.keyTakeaways.description}
                        </Typography>
                    )}

                    {/* Recommendation levels */}
                    {update.keyTakeaways.levels.map((level, idx) => (
                        <Paper
                            key={idx}
                            elevation={0}
                            sx={{
                                p: 2.5,
                                mb: 2,
                                borderRadius: 2,
                                backgroundColor: level.bgColor,
                                border: '1px solid',
                                borderColor: level.borderColor
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                                <Chip
                                    label={level.level}
                                    size="small"
                                    sx={{
                                        fontWeight: 700,
                                        backgroundColor: level.color,
                                        color: 'white',
                                        fontSize: '0.75rem'
                                    }}
                                />
                            </Box>
                            <Box component="ul" sx={{ m: 0, pl: 3 }}>
                                {level.items.map((item, i) => {
                                    const parts = item.split(' — ');
                                    return (
                                        <Typography component="li" key={i} variant="body2" sx={{ mb: 0.5, color: '#374151' }}>
                                            <strong>{parts[0]}</strong>
                                            {parts[1] && (
                                                <Typography component="span" variant="body2" sx={{ fontStyle: 'italic', color: '#6b7280' }}>
                                                    {' — '}{parts[1]}
                                                </Typography>
                                            )}
                                        </Typography>
                                    );
                                })}
                            </Box>
                        </Paper>
                    ))}

                    {/* Against recommendations */}
                    {update.keyTakeaways.againstRecommendations.length > 0 && (
                        <>
                            <Divider sx={{ my: 2.5 }} />
                            <Paper
                                elevation={0}
                                sx={{
                                    p: 2.5,
                                    mb: 2,
                                    borderRadius: 2,
                                    backgroundColor: '#fef2f2',
                                    border: '1px solid',
                                    borderColor: '#fecaca'
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                                    <WarningAmberIcon sx={{ color: '#dc2626', fontSize: 20 }} />
                                    <Typography variant="subtitle2" fontWeight="700" sx={{ color: '#dc2626' }}>
                                        Recommendations Against Use
                                    </Typography>
                                </Box>
                                {update.keyTakeaways.againstRecommendations.map((rec, i) => (
                                    <Typography key={i} variant="body2" sx={{ mb: 0.5, color: '#374151', pl: 1 }}>
                                        <Chip
                                            label={rec.level}
                                            size="small"
                                            sx={{
                                                fontWeight: 700,
                                                backgroundColor: '#dc2626',
                                                color: 'white',
                                                fontSize: '0.7rem',
                                                height: 20,
                                                mr: 1
                                            }}
                                        />
                                        <strong>{rec.medication}</strong> — <em>{rec.label}</em>
                                    </Typography>
                                ))}
                            </Paper>
                        </>
                    )}

                    {/* Provider notes */}
                    {update.keyTakeaways.providerNotes.length > 0 && (
                        <>
                            <Divider sx={{ my: 2.5 }} />
                            <Paper
                                elevation={0}
                                sx={{
                                    p: 2.5,
                                    borderRadius: 2,
                                    backgroundColor: '#f0f9ff',
                                    border: '1px solid',
                                    borderColor: '#bae6fd'
                                }}
                            >
                                <Typography variant="subtitle2" fontWeight="700" sx={{ color: '#0369a1', mb: 1 }}>
                                    Provider Reminders
                                </Typography>
                                <Box component="ul" sx={{ m: 0, pl: 3 }}>
                                    {update.keyTakeaways.providerNotes.map((note, i) => (
                                        <Typography component="li" key={i} variant="body2" sx={{ mb: 0.5, color: '#374151' }}>
                                            {note}
                                        </Typography>
                                    ))}
                                </Box>
                            </Paper>
                        </>
                    )}
                </Paper>

                {/* Detailed recommendations */}
                <Accordion
                    elevation={0}
                    sx={{
                        borderRadius: 2,
                        '&:before': { display: 'none' },
                        border: '1px solid',
                        borderColor: 'divider',
                        overflow: 'hidden'
                    }}
                >
                    <AccordionSummary
                        expandIcon={<ExpandMoreIcon sx={{ color: 'white' }} />}
                        sx={{
                            backgroundColor: teal,
                            color: 'white',
                            '&:hover': { backgroundColor: teal }
                        }}
                    >
                        <Typography fontWeight="600">Detailed recommendations</Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 3 }}>
                        {update.detailedRecommendationsIntro && (
                            <Typography variant="body1" color="text.secondary" mb={2}>
                                {update.detailedRecommendationsIntro}
                            </Typography>
                        )}
                        {typeof update.detailedRecommendations === 'string' ? (
                            <Typography variant="body1" color="text.secondary">
                                {update.detailedRecommendations}
                            </Typography>
                        ) : (
                            update.detailedRecommendations.map((section, idx) => (
                                <Box key={idx} sx={{ mb: idx < update.detailedRecommendations.length - 1 ? 3 : 0 }}>
                                    <Typography variant="subtitle2" fontWeight="600" mb={1}>
                                        {section.header}
                                    </Typography>
                                    <Box component="ul" sx={{ m: 0, pl: 3 }}>
                                        {section.items.map((item, i) => (
                                            <Typography component="li" key={i} variant="body2" sx={{ mb: 0.5, color: '#374151' }}>
                                                {item}
                                            </Typography>
                                        ))}
                                    </Box>
                                </Box>
                            ))
                        )}
                    </AccordionDetails>
                </Accordion>

                {update.highRiskGroups && (
                    <Paper elevation={0} sx={{ p: { xs: 2, md: 3 }, mt: 3, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                        <TableContainer>
                            <Table size="small" aria-label="High-risk groups for influenza complications">
                                <caption style={{ captionSide: 'top', textAlign: 'left', fontWeight: 600, color: '#2C3E50' }}>
                                    Table 2. High-Risk Groups for Influenza Complications
                                </caption>
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 600 }}>Category</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Description / common examples (c)</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {update.highRiskGroups.map((group) => (
                                        <TableRow key={group.category}>
                                            <TableCell component="th" scope="row" sx={{ verticalAlign: 'top', width: '35%' }}>{group.category}</TableCell>
                                            <TableCell>
                                                {group.descriptions.map((description) => (
                                                    <Typography key={description} variant="body2" sx={{ mb: 0.5 }}>{description}</Typography>
                                                ))}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <Box sx={{ mt: 2 }}>
                            {update.highRiskNotes.map((note) => (
                                <Typography key={note} variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>{note}</Typography>
                            ))}
                        </Box>
                    </Paper>
                )}

                {/* Source */}
                <Paper
                    elevation={0}
                    sx={{
                        p: 3,
                        mt: 3,
                        borderRadius: 2,
                        backgroundColor: '#f8f9fa',
                        border: '1px solid',
                        borderColor: 'divider'
                    }}
                >
                    <Typography variant="subtitle1" fontWeight="600" mb={1}>
                        Sources
                    </Typography>
                    <Typography
                        component="a"
                        href={update.sourceUrl || update.officialGuidelineUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        variant="body1"
                        sx={{ color: teal, textDecoration: 'underline' }}
                    >
                        {update.sourceUrl || update.officialGuidelineUrl}
                    </Typography>
                </Paper>
            </Box>
        </Box>
    );
}
